import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { listarConfirmacoesPresenca } from "../../services/eventosStore";
import "./funcionalidadesEvento.scss";

// A confirmação é gravada em UTC. Se o backend mandar o instante sem fuso
// ("2026-10-14T22:30:00"), o JS o leria como horário local e o dia exibido
// andaria 3h — o mesmo tipo de erro que o parseDateLocal evita do outro lado.
// Assume UTC quando o fuso não vem; a exibição segue no fuso do usuário.
function formatarData(iso) {
  if (!iso) return "";
  const raw = String(iso).trim();
  const temFuso = /(Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const d = new Date(temFuso ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1,
  ).padStart(2, "0")}`;
}

// Lista de quem confirmou presença, sempre separada por turma: um evento pode
// ir para várias turmas e o total somado não serve para conferência.
export default function ModalConfirmacoes({ aberto, eventoId, onFechar }) {
  const [expandido, setExpandido] = useState(false);
  const [turmas, setTurmas] = useState([]);
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    if (!aberto || !eventoId) return;
    let vivo = true;

    setExpandido(false);
    setErro(false);

    listarConfirmacoesPresenca(eventoId)
      .then((lista) => {
        if (!vivo) return;
        setTurmas(lista);
        setTurmaSelecionadaId(lista[0]?.turmaId ?? null);
      })
      .catch(() => {
        if (vivo) setErro(true);
      });

    return () => {
      vivo = false;
    };
  }, [aberto, eventoId]);

  const turmaSelecionada = useMemo(
    () =>
      turmas.find((t) => String(t.turmaId) === String(turmaSelecionadaId)) ||
      null,
    [turmas, turmaSelecionadaId],
  );

  // Fechado mostra o total geral; aberto acompanha a turma selecionada.
  const contador = expandido
    ? (turmaSelecionada?.total ?? 0)
    : turmas.reduce((soma, t) => soma + (t.total || 0), 0);

  if (!aberto) return null;

  return (
    <div className="func-modal-overlay" role="presentation" onClick={onFechar}>
      <div
        className="func-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Confirmações de presença"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="func-modal__close"
          onClick={onFechar}
          aria-label="Fechar"
        >
          <X size={16} />
        </button>

        <button
          type="button"
          className="func-modal__accordion"
          onClick={() => setExpandido((v) => !v)}
          aria-expanded={expandido}
        >
          {expandido ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span>Confirmação de presença</span>
          <span className="func-modal__contador">{contador}</span>
        </button>

        {expandido && erro && (
          <div className="func-modal__vazio">
            Não foi possível carregar as confirmações.
          </div>
        )}

        {expandido && !erro && (
          <>
            <div className="func-modal__chips">
              {turmas.map((t) => (
                <button
                  key={t.turmaId}
                  type="button"
                  className={`func-modal__chip ${
                    String(t.turmaId) === String(turmaSelecionadaId)
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() => setTurmaSelecionadaId(t.turmaId)}
                >
                  {t.turmaNome}
                </button>
              ))}
            </div>

            {turmaSelecionada?.confirmados?.length ? (
              <div className="func-modal__lista">
                {turmaSelecionada.confirmados.map((c) => (
                  <div className="func-modal__item" key={c.userId}>
                    <span className="func-modal__itemData">
                      {formatarData(c.confirmadoEm)}
                    </span>
                    <span className="func-modal__itemNome">{c.nome}</span>
                    <span className="func-modal__itemLogin">{c.email}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="func-modal__vazio">
                Nenhum aluno confirmou presença ainda.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
